import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import VisibilitySensor from 'react-visibility-sensor';
import { withTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import CreateReusableAsset from '../../components/CreateReusableAssetModal/ReusableAssetModal';
import { COMPLEX_DOCUMENT_VERSION } from '../../utils/complexDocumentConstants';
import { FullPageLoaderContainer } from '../../artboard/styles';
import Loader from '../../components/Loader';
import { BusinessLogic } from '../../context/businessLogicContext';
import { useSessionContext } from '../../context/sessionContext';
import { DOCUMENT_DESIGN_SECTION } from '../../artboard/translationValues';
import { checkLockStatus, patchComplexDocument, lockResource, unlockResource } from '../../utils/services/complexDocumentsAPI';
import { COMPLEX_DOCUMENT_CONTEXT_KEYS, useDocumentContext } from '../../context/complexDocumentContext';
import { CONTEXT_KEYS, TEMPLATE_TYPE, useArtboardContext } from '../../context/complexArtboardContext';
import { useEmailContext } from '../../context/complexEmailContext';
import ConfirmationPageDialog from '../../components/Modals/ConfirmationPageDialog';
import TemplateLockedModal from '../ComplexDocumentDesign/TemplateLockedModal';
import UpgradePlanDesign from '../ComplexDocumentDesign/UpgradePlanDesign';
import UserActivityListener from '../ComplexDocumentDesign/UserActivityListener';
import ComplexEmailPage from './ComplexEmailPage';
import ComplexEmailSubjectEditor from './ComplexEmailSubjectEditor';
import { getComplexEmail, getComplexEmailStatus, getEmailStandaloneBlock, getStandaloneEmailBlockStatus } from '../../utils/services/complexEmailAPI';
import EmailPreviewModal from '../../components/Modals/EmailPreviewModal';
import ComplexEmailLayoutSettings from './ComplexEmailLayoutSettings';
import { useDefaultStyleContext } from '../../context/complexDefaultStyleContext';
import { DEFAULT_FONT_FOR_LOCALISED_LANGUAGES, checkUserAccessRoles } from '../../utils/helper';
import { getIndexedDBData } from '../../utils/indexedDBUtil';
import { BusinessLogicModal } from '../../components/BusinessLogicModal';

const ComplexEmailArtboard = ({ t, i18n, templateId, visibilityRef, existingDocument = undefined, version = COMPLEX_DOCUMENT_VERSION.DRAFT , EmailTemplateTab, setEnvEmailData}) => {
  const customSelector = useCallback((store) => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.FLOWS], []);
  const [flows, store] = useDocumentContext(customSelector, 'ComplexArtboard');

  const customVariableSelector = useCallback(store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.FETCHING_GLOBAL_VARIABLES], []);
  const [isFetchingGlobalVariables] = useDocumentContext(customVariableSelector);

  const editableSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN], []);
  const [editable] = useArtboardContext(editableSelector, 'ComplexArtboard');
  
  const lockedUserIdSelector = useCallback(store => store[CONTEXT_KEYS.LOCKED_USER_ID], []);
  const [lockedUserID] = useArtboardContext(lockedUserIdSelector, `ComplexArtboard`);

  const customArtboardSelector = useCallback(store => store[CONTEXT_KEYS.SCALING_SETTING], []);
  const [scaling,,resetArtboard] = useArtboardContext(customArtboardSelector, 'ComplexArtboard');

  const customLoaderSelector = useCallback(store => store[CONTEXT_KEYS.LOADING_DESIGN], []);
  const [isDesignLoading, updateArtboard] = useArtboardContext(customLoaderSelector);

  const getCurrentDesign = useCallback(store => store[CONTEXT_KEYS.CURRENT_DESIGN], []);
  const [CurrentDesign] = useArtboardContext(getCurrentDesign, 'ComplexArtboard');

  const reusableAssetSelector = useCallback((store) => store[CONTEXT_KEYS.REUSABLE_ASSET_MODAL], []);
  const [reusableAssetModal] = useArtboardContext(reusableAssetSelector, 'ComplexArtboard');

  const newFlowDataSelector = useCallback((store) => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.REUSABLE_ASSERT_DATA], []);
  const [reusableAssertData] = useDocumentContext(newFlowDataSelector, 'ComplexArtboard');

  const previewDetailSelector = useCallback(store => store[CONTEXT_KEYS.PREVIEW_MODAL_DETAILS], []);
  const [PreviewModalDetails] = useArtboardContext(previewDetailSelector, `ComplexArtboard`);

  const aiSelector = useCallback(store => store[CONTEXT_KEYS.AI_LOADER], []);
  const [aiLoader] = useArtboardContext(aiSelector, `ComplexArtboard`);

  const [modalInfo] = useArtboardContext(store => store[CONTEXT_KEYS.BUSINESS_LOGIC_MODAL]);
  const businessLogicModalVisible = modalInfo ? Object.keys(modalInfo).length > 0 : false;

  const { setBusinessLogics } = useContext(BusinessLogic);

  const sessionSelector = useCallback(store => store.userData, []);
  const [userDetails] = useSessionContext(sessionSelector);

  const previewSelector = useCallback(store => store[CONTEXT_KEYS.ENABLE_PAGE_OVERFLOW], []);
  const [isPreview] = useArtboardContext(previewSelector);

  const hightlightIdSelector = useCallback((store) => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.GROUP_HIGHLIGHT_ID], []);
  const [highlightId] = useDocumentContext(hightlightIdSelector, 'ComplexArtboard');

  const dummySelector = useCallback(() => undefined, []);
  const [,emailMethods] = useEmailContext(dummySelector, 'ComplexEmailArtboard');
  const [ , defaultStyleStore ] = useDefaultStyleContext(()=> null);
  const currentLanguage = i18n.language;
  const history = useHistory();
  
  const { templateId: documentId } = useParams();

  const [showLockedModal, setShowLockedModal] = useState(false);
  const [lockedUserName, setLockedUserName] = useState('');
  const payloadRef = useRef({});
  const lockedUserIDRef = useRef(lockedUserID);

  useEffect(() => {
    lockedUserIDRef.current = lockedUserID;
  }, [lockedUserID]);

  const highlightTimerRef = useRef(null);
  useEffect(() => {
    if (highlightId) {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
      }
      highlightTimerRef.current = setTimeout(() => {
        store.set({
          [COMPLEX_DOCUMENT_CONTEXT_KEYS.GROUP_HIGHLIGHT_ID]: null,
        });
        highlightTimerRef.current = null;
      }, 3000);
    } else {
      if (highlightTimerRef.current) {
        clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = null;
      }
    }
  }, [highlightId]);

  useEffect(() => {
    if (templateId === undefined) return;
    let extraArtboardContextChanges = {};
    (async () => {
      let templateType = documentId ? TEMPLATE_TYPE.DOCUMENT : TEMPLATE_TYPE.BLOCK;   
      updateArtboard({ [CONTEXT_KEYS.CURRENT_DESIGN]: templateType, [CONTEXT_KEYS.LOADING_DESIGN]: true });

      if(templateType === "document") {
        const existingDocument = await getComplexEmail(templateId, version);
         if(existingDocument.statusCode === 707) {
          existingDocument.data.documentId = templateId;
          store.setComplexDocumentDesign(existingDocument.data, true, templateType);
          defaultStyleStore.set({
            fontFamily: existingDocument?.data?.flows[0]?.style?.fontFamily || DEFAULT_FONT_FOR_LOCALISED_LANGUAGES(currentLanguage),
            fontSize: existingDocument?.data?.flows[0]?.style?.fontSize || 16,
          });
          setBusinessLogics(existingDocument.data.businessLogics);
          emailMethods.setInitialState(existingDocument.data);
          setEnvEmailData && setEnvEmailData(existingDocument.data);
          if (version === COMPLEX_DOCUMENT_VERSION.DRAFT) {
            // TODO : Acquire lock for the design to enable or disable readOnly mode
            // TODO : Get status of document          
            const documentTimeSpanResponse = await getComplexEmailStatus(templateId);
            if (documentTimeSpanResponse?.statusCode === 3112) {
              extraArtboardContextChanges = {
                ...extraArtboardContextChanges,
                [CONTEXT_KEYS.LIFECYCLE_TIMELINE]: documentTimeSpanResponse.data
              };
            }
          }
         } else {
           if (version === COMPLEX_DOCUMENT_VERSION.DRAFT) {
             history.push('/notfound');
             return;
           } else {
             console.log("existingDocument :: ", existingDocument);
           }
         }
      } else if(templateType === 'block') {
        const existingDocument = await getEmailStandaloneBlock(templateId, version);       
        if(existingDocument.statusCode === 2505) {
          store.setComplexDocumentDesign(existingDocument.data);
          setEnvEmailData && setEnvEmailData(existingDocument.data);
          defaultStyleStore.set({
            fontFamily: existingDocument?.data?.style?.fontFamily || DEFAULT_FONT_FOR_LOCALISED_LANGUAGES(currentLanguage),
            fontSize: existingDocument?.data?.style?.fontSize || 16,
          });
          const flowTimeSpanResponse = await getStandaloneEmailBlockStatus(templateId);
          if (flowTimeSpanResponse?.statusCode === 1927) {
            extraArtboardContextChanges = {
              ...extraArtboardContextChanges,
              [CONTEXT_KEYS.LIFECYCLE_TIMELINE]: flowTimeSpanResponse.data
            };
          }
        } else {
          history.push('/notfound');
          return;
        }
      }

      if(templateType === 'block' && !checkUserAccessRoles(userDetails, ['REUSABLECONTENTMANAGER'])) {
        updateArtboard({
          ...extraArtboardContextChanges,
          [CONTEXT_KEYS.TEMPLATE_LOCKED]: true,
          [CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN]: false,
          [CONTEXT_KEYS.LOADING_DESIGN]: false,
        });
        return;
      }

    if (version === COMPLEX_DOCUMENT_VERSION.DRAFT) {
      let payload = {};
      payload.resourceId = templateId;
      payload.resourceType = templateType === 'document' ? 'email' : templateType === 'block' ? 'email_block' : undefined;
  
      payloadRef.current = payload;

      if (payload.resourceType !== undefined) {
        const response = await checkLockStatus(payload);
        if (response) {
          if(response.statusCode === 740) {
            // Lock the template
            const lockResponse = await lockResource(payload);
            if(lockResponse.statusCode === 719) {
              extraArtboardContextChanges = {
                ...extraArtboardContextChanges,
                [CONTEXT_KEYS.LOCKED_USER_ID]: lockResponse.data.userId
              };
            }
          } else if(response.statusCode === 741) {
            extraArtboardContextChanges = {
              ...extraArtboardContextChanges,
              [CONTEXT_KEYS.LOCKED_USER_ID]: response.data.userId
            };
            /* Check if the same user is trying to access the template,
            if not show template is locked modal and render the template in read-only mode */
            if(userDetails.id !== response.data.userId) {
              setLockedUserName(response.data.userName);
              setShowLockedModal(true);
              extraArtboardContextChanges = {
                ...extraArtboardContextChanges,
                [CONTEXT_KEYS.TEMPLATE_LOCKED]: true,
                [CONTEXT_KEYS.ENABLE_EDITABLE_DESIGN]: false
              };
            }
          }
        }
      }
    }
      if(!navigator.onLine) {
        const currentDocument = await getIndexedDBData('complexDocument');
        store.setComplexDocumentDesign(currentDocument);
      }
      updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: false, ...extraArtboardContextChanges });
    })();
  }, [templateId, version]);

  useEffect(() => {
    if (existingDocument === undefined) return;
    store.setComplexDocumentDesign(existingDocument, false, CurrentDesign);
    defaultStyleStore.set({
      fontFamily: existingDocument?.style?.fontFamily || existingDocument?.flows[0]?.style?.fontFamily || DEFAULT_FONT_FOR_LOCALISED_LANGUAGES(currentLanguage),
      fontSize: existingDocument?.style?.fontSize || existingDocument?.flows[0]?.style?.fontSize || 16,
    });
    emailMethods.setInitialState(existingDocument, false);
    setBusinessLogics(existingDocument.businessLogics);
    updateArtboard({ [CONTEXT_KEYS.LOADING_DESIGN]: false });
  }, [existingDocument]);

  useEffect(() => {
    // remove datasetCache on mount so it will be removed on refresh/go back in browser history.
    // so latest variables for the document is re-generated.
    // don't remove datasetCache on preview mode.
    !isPreview && sessionStorage.removeItem('datasetCache');
    const updateVariables = () => {
      store.fetchAllVariables();
    }
    window.addEventListener("variablesUpdated", updateVariables);

    return () => {
      console.log("Unmounting artboard!!");
      store.clearDesign();
      resetArtboard();
      window.removeEventListener("variablesUpdated", updateVariables);
    }
  }, []);

  useEffect(() => {
    return async () => {
      // Unlock the template on component unmount
      if (userDetails.id === lockedUserIDRef.current) {
        await unlockResource(payloadRef.current);
      }
    }
  }, [documentId]);

  const onCancelBusinessLogicModal = () => {
    updateArtboard({ [CONTEXT_KEYS.BUSINESS_LOGIC_MODAL]: undefined });
  };

  const disableModalVisibility = () => {
    store.set({ [COMPLEX_DOCUMENT_CONTEXT_KEYS.REUSABLE_ASSERT_DATA]: {} });
    updateArtboard({ [CONTEXT_KEYS.REUSABLE_ASSET_MODAL]: false })  
  };

  const updateFlow = async(flowId,isAttach,flowUUID) => {
    disableModalVisibility();
    if(isAttach){
      const payload =  store.createDocumentPayload(flowId,flowUUID);
      await patchComplexDocument(payload,flowUUID)
    }
  };

  const disablePreviewModal = () => {
    updateArtboard({ [CONTEXT_KEYS.PREVIEW_MODAL_DETAILS]: { visible: false, } })
  }

   return (
    <>
      {(isFetchingGlobalVariables || isDesignLoading) ? (
        <FullPageLoaderContainer>
          <Loader fullpage="true" />
        </FullPageLoaderContainer>
      ) : (
        <>
          {flows.map((flow, flowNumber) => (
            <>
              <VisibilitySensor
                key={flow.uuid}
                containment={visibilityRef?.current}
                partialVisibility={true}
                offset={{ top: 150 }} // TODO : Check if this has to be configured by page size or orientation
                minTopValue={400}
              >
                {({ isVisible }) => {
                  return (
                    <ComplexEmailPage
                      key={`${flow}-${flowNumber}`}
                      uuid={flow}
                      flowNumber={flowNumber}
                      totalPages={flows.length}
                      scaling={scaling}
                      isVisible={isVisible}
                      visibilityRef={visibilityRef?.current}
                      isReadOnlyMode={editable}
                      EmailTemplateTab={EmailTemplateTab}
                    />
                  );
                }}
              </VisibilitySensor>
            </>
          ))}
          <ComplexEmailLayoutSettings />
          <ConfirmationPageDialog isEmailTemplate={true}/>
          {businessLogicModalVisible && <BusinessLogicModal modalInfo= {modalInfo} onCancel= {onCancelBusinessLogicModal}/> }
          {reusableAssetModal &&
            <CreateReusableAsset
              onSucess={updateFlow}
              onCancel={disableModalVisibility}                
              type={reusableAssertData.type}
              isEmailTemplate={true}
            />
          }
          <UserActivityListener checkLockStatus={() => checkLockStatus(payloadRef.current)} />
          <UpgradePlanDesign />
        </>
      )}    
      { showLockedModal && <TemplateLockedModal
          visible={showLockedModal}
          onClose={() => setShowLockedModal(false)}
          lockedByUser={lockedUserName}
      /> }
       {PreviewModalDetails?.visible &&
         <EmailPreviewModal
           visible={PreviewModalDetails.visible}
           onCancel={disablePreviewModal}
           type={'flow'}          
           templateId={PreviewModalDetails.flowId}
           templateData={PreviewModalDetails.flowData || undefined}
           reusableFlow={PreviewModalDetails.isStandalone}
         />
       }
       {aiLoader && (
        <FullPageLoaderContainer>
          <Loader
            text={t(
              DOCUMENT_DESIGN_SECTION.AI_AUTO_FILL_CONTENT_MODAL_INFO.key,
              DOCUMENT_DESIGN_SECTION.AI_AUTO_FILL_CONTENT_MODAL_INFO.value,
            )}
          />
        </FullPageLoaderContainer>
      )}
      <ComplexEmailSubjectEditor />
    </>
  );
}

export default React.memo(withTranslation(['COMPLEX_PAGE_OPTIONS'])(ComplexEmailArtboard));